// src/pages/DayAccounting.js — Day Accounting (v2, 6 Oct 2026)
// One report keyed on the invoice BILL date (IST):
//   1. Not completed: bills of the date range not yet delivered
//   2. Vouchers pending: delivered bills whose voucher is not back, by driver and round
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { get } from '../utils/api';

const STAGE_LABELS = {
  stage_1_erp: 'ERP raised',
  stage_2_stock_removal: 'Stock removal',
  stage_3_checking: 'Checking',
  stage_4_packing: 'Ready for delivery',
  stage_5_dispatched: 'In transit',
  reattempt_pending: 'Reattempt',
  returned: 'Returned',
};

const STAGE_COLORS = {
  stage_1_erp: 'bg-yellow-100 text-yellow-800',
  stage_2_stock_removal: 'bg-yellow-100 text-yellow-800',
  stage_3_checking: 'bg-blue-100 text-blue-800',
  stage_4_packing: 'bg-indigo-100 text-indigo-800',
  stage_5_dispatched: 'bg-blue-100 text-blue-800',
  reattempt_pending: 'bg-red-100 text-red-800',
  returned: 'bg-red-100 text-red-800',
};

const OUTCOME_LABELS = {
  signed: 'Signed',
  neighbour: 'Neighbour',
  cash: 'Cash',
  cheque: 'Cheque',
  upi: 'UPI',
  retained_by_party: 'Retained by party',
  rectification: 'Rectification',
};

function istToday() {
  return new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}

function fmtBill(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function fmtDay(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', {
    timeZone: 'UTC', day: 'numeric', month: 'short',
  });
}

function fmtAgeHours(h) {
  return h < 24 ? `${h} h` : `${Math.floor(h / 24)} d`;
}

function fmtAgeDays(d) {
  if (d === 0) return 'today';
  return d === 1 ? '1 day' : `${d} days`;
}

function ageDaysClass(d) {
  if (d >= 3) return 'bg-red-100 text-red-800';
  if (d === 2) return 'bg-yellow-100 text-yellow-800';
  return 'text-gray-700 dark:text-gray-300';
}

function partyLine(r) {
  return [r.city, r.area_name].filter(Boolean).join(', ');
}

function carryLabel(c) {
  if (!c) return '';
  return c.state === 'overdue'
    ? `Overdue since 11 am ${fmtDay(c.due_date)}`
    : `Carry over, due 11 am ${fmtDay(c.due_date)}`;
}

function downloadCsv(data) {
  const q = String.fromCharCode(34);
  const esc = (v) => q + String(v === null || v === undefined ? '' : v).split(q).join(q + q) + q;
  const rows = [[
    'Section', 'Bill no', 'Bill date and time (IST)', 'Party', 'City', 'Area',
    'Stage or outcome', 'Driver', 'Round', 'Voucher status', 'Age', 'Flag',
  ]];
  data.not_completed.forEach((r) => rows.push([
    'Not completed', r.invoice_number, fmtBill(r.invoice_date), r.party_name, r.city, r.area_name,
    STAGE_LABELS[r.status] || r.status, r.driver_name, r.round_label || r.round_number, '', fmtAgeHours(r.age_hours), carryLabel(r.carry_over),
  ]));
  data.voucher_groups.forEach((g) => g.rounds.forEach((rd) => rd.vouchers.forEach((v) => rows.push([
    'Voucher pending', v.invoice_number, fmtBill(v.invoice_date), v.party_name, v.city, v.area_name,
    OUTCOME_LABELS[v.outcome] || v.outcome, g.driver_name, rd.round_label || rd.round_number, v.voucher_status || 'Not set', fmtAgeDays(v.age_days), '',
  ]))));
  const csv = rows.map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `day-accounting-${data.from}${data.to !== data.from ? '_to_' + data.to : ''}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Card({ label, value, tone }) {
  return (
    <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-2xl font-medium ${tone || 'text-gray-900 dark:text-gray-100'}`}>{value}</div>
    </div>
  );
}

export default function DayAccounting({ embedded = false, deliveryType = 'all' }) {
  const [from, setFrom] = useState(istToday());
  const [to, setTo] = useState(istToday());
  const [includeEarlier, setIncludeEarlier] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const hasData = useRef(false);
  // Everything starts closed. 'open' holds the section heads and driver heads the
  // user has opened (keys: 's1', 's2', 'g:<driver id>'). Rounds start open once their
  // driver is opened, so 'closedRounds' holds only the rounds the user has closed.
  const [open, setOpen] = useState(new Set());
  const [closedRounds, setClosedRounds] = useState(new Set());

  const flip = (setter, key) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleOpen = (key) => flip(setOpen, key);
  const toggleRound = (key) => flip(setClosedRounds, key);
  const expandAll = () => {
    if (!data) return;
    setOpen(new Set(['s1', 's2', ...data.voucher_groups.map((g) => 'g:' + g.delivery_person_id)]));
    setClosedRounds(new Set());
  };
  const collapseAll = () => setOpen(new Set());

  const load = useCallback(async () => {
    if (!hasData.current) setLoading(true);
    else setRefreshing(true);
    setError('');
    try {
      const d = await get(`/api/vouchers/day-accounting?from=${from}&to=${to}&include_earlier=${includeEarlier ? 1 : 0}&delivery_type=${deliveryType}`);
      setData(d);
      hasData.current = true;
    } catch (err) {
      setError(err.message || 'Could not load the report');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [from, to, includeEarlier, deliveryType]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const th = 'text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-3 py-2 border-b border-gray-300 dark:border-gray-600 whitespace-nowrap';
  const td = 'px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-sm align-top';

  return (
    <div className={(embedded ? 'py-4' : 'p-6 max-w-6xl mx-auto') + ' dark:text-gray-100'}>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-lg font-medium mr-2">Day accounting</h1>
        <label className="text-sm text-gray-600 dark:text-gray-300">From</label>
        <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800" />
        <label className="text-sm text-gray-600 dark:text-gray-300">To</label>
        <input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800" />
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={includeEarlier} onChange={(e) => setIncludeEarlier(e.target.checked)} />
          Include vouchers from earlier bill dates
        </label>
        <div className="ml-auto flex items-center gap-3">
          {refreshing && <span className="text-xs text-gray-500">Updating…</span>}
          <button onClick={expandAll} className="border border-gray-300 dark:border-gray-600 rounded px-3 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Expand all</button>
          <button onClick={collapseAll} className="border border-gray-300 dark:border-gray-600 rounded px-3 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Collapse all</button>
          <button onClick={load} className="border border-gray-300 dark:border-gray-600 rounded px-3 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Refresh</button>
          <button onClick={() => data && downloadCsv(data)} className="border border-gray-300 dark:border-gray-600 rounded px-3 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Export CSV</button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-100 text-red-800 px-4 py-3 text-sm">{error}</div>
      )}
      {loading && !data && <div className="text-sm text-gray-500">Loading…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card label="Bills in this range" value={data.summary.total} />
            <Card label="Completed" value={data.summary.completed} tone="text-green-700" />
            <Card label="Not completed" value={data.summary.not_completed} tone="text-red-700" />
            <Card label="Vouchers pending" value={data.summary.vouchers_pending} tone="text-yellow-700" />
          </div>

          <div className="mb-6">
            <button type="button" onClick={() => toggleOpen('s1')} aria-expanded={open.has('s1')}
              className="w-full flex items-center justify-between rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2.5 text-left mb-2">
              <span className="flex items-center gap-2 text-sm font-medium text-red-900 dark:text-red-100">
                <span className="inline-block w-4 text-xs">{open.has('s1') ? '\u25BC' : '\u25B6'}</span>
                1. Not completed ({data.not_completed.length})
              </span>
              <span className="text-xs text-red-800 dark:text-red-200">Bills not yet delivered</span>
            </button>
            {open.has('s1') && (
              <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Bills in this range that have not been delivered, oldest bill first. In transit after 10 pm carries over to 11 am the next day.
          </p>
          <div className="overflow-x-auto mb-4">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr>
                  <th className={th}>Bill no.</th>
                  <th className={th}>Bill date and time</th>
                  <th className={th}>Party</th>
                  <th className={th}>Stage</th>
                  <th className={th}>With</th>
                  <th className={th}>Age</th>
                  <th className={th}>Flag</th>
                </tr>
              </thead>
              <tbody>
                {data.not_completed.length === 0 && (
                  <tr><td className={td} colSpan={7}>Every bill in this range is delivered.</td></tr>
                )}
                {data.not_completed.map((r) => (
                  <tr key={r.invoice_id}>
                    <td className={td + ' font-medium'}>{r.invoice_number}</td>
                    <td className={td + ' whitespace-nowrap'}>{fmtBill(r.invoice_date)}</td>
                    <td className={td}>
                      <div>{r.party_name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{partyLine(r)}</div>
                    </td>
                    <td className={td}>
                      <span className={`inline-block rounded px-2 py-0.5 text-xs ${STAGE_COLORS[r.status] || 'bg-gray-100 text-gray-800'}`}>
                        {STAGE_LABELS[r.status] || r.status}
                      </span>
                    </td>
                    <td className={td}>
                      {r.driver_name ? `${r.driver_name}${(r.round_label || r.round_number) ? `, Round ${r.round_label || r.round_number}` : ''}` : '-'}
                    </td>
                    <td className={td + ' whitespace-nowrap'}>{fmtAgeHours(r.age_hours)}</td>
                    <td className={td}>
                      {r.carry_over && (
                        <span className={`inline-block rounded px-2 py-0.5 text-xs ${r.carry_over.state === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {carryLabel(r.carry_over)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
              </>
            )}
          </div>

          <div>
            <button type="button" onClick={() => toggleOpen('s2')} aria-expanded={open.has('s2')}
              className="w-full flex items-center justify-between rounded-md bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 px-3 py-2.5 text-left mb-2">
              <span className="flex items-center gap-2 text-sm font-medium text-yellow-900 dark:text-yellow-100">
                <span className="inline-block w-4 text-xs">{open.has('s2') ? '\u25BC' : '\u25B6'}</span>
                2. Vouchers pending ({data.summary.vouchers_pending})
              </span>
              <span className="text-xs text-yellow-800 dark:text-yellow-200">Delivered, voucher not back</span>
            </button>
            {open.has('s2') && (
              <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Delivered bills whose voucher is not back at the office. By driver (oldest bill first), then by round.
          </p>
          {data.voucher_groups.length === 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-300">No vouchers pending for this range.</div>
          )}
          {data.voucher_groups.map((g) => {
            const gKey = 'g:' + g.delivery_person_id;
            const gClosed = !open.has(gKey);
            return (
              <div key={g.delivery_person_id} className="mb-5">
                <button type="button" onClick={() => toggleOpen(gKey)} aria-expanded={!gClosed}
                  className="w-full flex items-center justify-between rounded-md bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 px-3 py-2 text-left">
                  <span className="flex items-center gap-2 text-sm font-medium text-blue-900 dark:text-blue-100">
                    <span className="inline-block w-4 text-xs">{gClosed ? '\u25B6' : '\u25BC'}</span>
                    {g.driver_name}
                  </span>
                  <span className="text-xs text-blue-800 dark:text-blue-200">
                    {g.total} {g.total === 1 ? 'voucher' : 'vouchers'}, oldest bill {fmtDay(new Date(new Date(g.oldest_bill_date).getTime() + 5.5 * 3600 * 1000).toISOString())}
                  </span>
                </button>
                {!gClosed && g.rounds.map((rd) => {
                  const rKey = 'r:' + g.delivery_person_id + ':' + (rd.round_id || 'none');
                  const rClosed = closedRounds.has(rKey);
                  return (
                    <div key={rd.round_id || 'none'} className="mt-2 ml-3">
                      <button type="button" onClick={() => toggleRound(rKey)} aria-expanded={!rClosed}
                        className="w-full flex items-center gap-2 rounded bg-gray-100 dark:bg-gray-800 px-3 py-1.5 text-left text-xs font-medium text-gray-700 dark:text-gray-200">
                        <span className="inline-block w-4">{rClosed ? '\u25B6' : '\u25BC'}</span>
                        <span>
                          {(rd.round_label || rd.round_number) ? `Round ${rd.round_label || rd.round_number}${rd.round_date ? `, ${fmtDay(rd.round_date)}` : ''}` : 'No round'}
                          {' \u00B7 '}{rd.vouchers.length} {rd.vouchers.length === 1 ? 'voucher' : 'vouchers'}
                        </span>
                      </button>
                      {!rClosed && (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[720px]">
                            <thead>
                              <tr>
                                <th className={th}>Bill no.</th>
                                <th className={th}>Bill date and time</th>
                                <th className={th}>Party</th>
                                <th className={th}>Driver outcome</th>
                                <th className={th}>Voucher status</th>
                                <th className={th}>Age</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rd.vouchers.map((v) => (
                                <tr key={v.voucher_id}>
                                  <td className={td + ' font-medium'}>{v.invoice_number}</td>
                                  <td className={td + ' whitespace-nowrap'}>{fmtBill(v.invoice_date)}</td>
                                  <td className={td}>
                                    <div>{v.party_name}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{partyLine(v)}</div>
                                  </td>
                                  <td className={td}>{OUTCOME_LABELS[v.outcome] || v.outcome}</td>
                                  <td className={td}>{v.voucher_status || <span className="text-gray-400">Not set</span>}</td>
                                  <td className={td + ' whitespace-nowrap'}>
                                    <span className={`inline-block rounded px-2 py-0.5 text-xs ${ageDaysClass(v.age_days)}`}>{fmtAgeDays(v.age_days)}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
